import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { injectIntl, intlShape } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { StaticPage, TopbarContainer } from '..';
import { NamedLink } from '../../components';
import {
  LayoutSingleColumn,
  LayoutWrapperMain,
  LayoutWrapperTopbar,
  LayoutWrapperFooter,
  Footer,
} from '../../components';
import config from '../../config';
import servicesImage from './NHS-50-232-Signalise-Co-op_AW_WEB cropped.jpeg';
import servicesQuoteImage from './Our_Services_Quote_202526.png';
import servicesSMSImage from './SMS_confirmation_example_reply_box_v2.0.jpg';

import css from './OurServicesPage.module.css';

const OurServicesPageComponent = props => {
  const { scrollingDisabled, intl } = props;

  const siteTitle = config.siteTitle;
  const schemaTitle = intl.formatMessage({ id: 'OurServicesPage.schemaTitle' }, { siteTitle });
  const schema = {
    '@context': 'https://schema.org',
    '@type': ['Service', 'WebPage'],
    name: 'Signalise Interpreting & Communication Services',
    description: 'High-quality BSL interpreting, deafblind support, video relay services, and communication support across health, education, social care and community settings. Co-operative model delivering real social value.',
    provider: {
      '@type': 'Organization',
      name: 'Signalise Co-op',
      url: 'https://signalise.coop',
      description: 'A co-operative owned and shaped by the people who use and deliver interpreting services',
      areaServed: {
        '@type': 'Country',
        name: 'United Kingdom'
      }
    },
    serviceType: [
      'British Sign Language (BSL) Interpreting',
      'Deafblind Interpreting',
      'Lipspeaking',
      'Note-taking',
      'Video Interpreting Services (VRI)',
      'Video Relay Services (VRS)',
      'Foreign Language Interpreting',
      'BSL Translation',
      'Speech to Text Reporting'
    ],
    areaServed: {
      '@type': 'Country',
      name: 'United Kingdom'
    },
    category: [
      'Accessibility Services',
      'Interpreting Services',
      'Communication Support',
      'Deaf Services'
    ],
    audience: {
      '@type': 'Audience',
      audienceType: [
        'Healthcare Organizations',
        'Educational Institutions',
        'Social Care Providers',
        'Public Sector',
        'Deaf Community',
        'Commissioners'
      ]
    },
    offers: {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      availableAtOrFrom: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          addressCountry: 'GB'
        }
      }
    }
  };
  return (
    <StaticPage title={schemaTitle} scrollingDisabled={scrollingDisabled} schema={schema}>
      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer currentPage="OurServicesPage" />
        </LayoutWrapperTopbar>
        <LayoutWrapperMain className={css.staticPageWrapper}>
          <div className={css.contentWrapper}>
            <div className={css.intro}>
              <h1 className={css.pageTitle}>Our Services</h1>
            </div>
             <div>
                 <nav className={css.tableOfContents}>
                <ul>
                  <li><a href="#download-overview">Download Our Services Overview (PDF)</a></li>
                  <li><a href="#interpreting-support">Interpreting & Communication Support</a></li>
                  <li><a href="#contracts-and-work">Our Contracts and Work</a></li>
                  <li><a href="#video-service">Our Video Service</a></li>
                  <li><a href="#cooperative-model">A Co-operative Model That Delivers Real Social Value</a></li>
                  <li><a href="#digital-booking">Digital Booking & Service Management</a></li>
                  <li><a href="#consultancy-training">Consultancy, Training & Service Design</a></li>
                </ul>
              </nav>
              </div>
            <div className={css.contentContainer}>
              <div className={css.contentMain}>
                <section className={css.contentSection}>
                  <h2 id="download-overview">Download Our Services Overview (PDF)</h2>
                  <p>
                    Get a clear, accessible summary of everything we offer — from on-site interpreting to video relay services — in one easy PDF. Perfect for commissioners, teams and organisational leads.
                  </p>
                  <a
                    href="https://files.signalise.coop/assets/Signalise_Services_Info_202526.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={css.downloadButton}
                  >
                    Download Services Overview
                  </a>
                  <img src={servicesImage} alt="Signalise Services" className={css.servicesImage} />

                  <h2 id="interpreting-support">Interpreting & Communication Support</h2>
                  <p>
                    We deliver high-quality interpreting and communication support across health, education, social care, community settings and any type of booking request.
                  </p>
                  <p>
                    We are one of the only organisations offering in person interpreting alongside on-demand and pre-booked video interpreting using our bespoke video service.
                    This flexibility means we can meet urgent needs, reduce waiting times and ensure your staff and Deaf users get the right support, when and where they need it.
                  </p>
                    
                  <p>Every booking is transparent, confirmed with Deaf users in advance, and matched to appropriately qualified professionals — reducing failed appointments and stress.
                  </p>
                  <p>Services include:</p>
                  <ul className={css.list}>
                    <li>British Sign Language (BSL) Interpreting</li>
                    <li>Foreign Language Interpreting</li>
                    <li>BSL Translators</li>
                    <li>Relay-Intralingual Interpreting</li>
                    <li>Speech to Text Reporters</li>
                    <li>Deafblind Interpreting</li>
                    <li>Lipspeaking</li>
                    <li>Note-taking (Manual & Electronic)</li>
                    <li>Video Interpreting / Video Relay Services (VRI & VRS)</li>
                  </ul>
                  <p>Read more about the <NamedLink name="CPTypesPage">
                  Communication Professional Types here</NamedLink>.
                  </p>
                    <img src={servicesQuoteImage} alt="Signalise Quote" className={css.servicesImage} />
                  
                  <h2 id="contracts-and-work">Our Contracts and Work</h2>
                  <p>
                    For the public sector, we are on several frameworks and are contract-ready. <a href="mailto:hello@signalise.coop">Get in touch to find out more</a> about our offerings through:
                    <ul className={css.list}>
                      <li><a href="https://www.sbs.nhs.uk/services/framework-agreements/interpretation-and-translation-services/" target="_blank" rel="noopener noreferrer">NHS SBS Framework</a> for Lot 2 British sign language Services </li>
                      <li><a href="https://www.healthtrusteurope.com/public-sector/total-language-solutions-ii/" target="_blank" rel="noopener noreferrer">Health Trust Europe Total Language Solutions II</a> for Lot 2 – Non-Spoken Interpretation and Translation Services (On-site and/or Remote)</li>
                      <li><a href="https://www.crowncommercial.gov.uk/agreements/RM6302" target="_blank" rel="noopener noreferrer">Crown Commercial Service (CCS)</a> Framework for BSL Interpreting Services under a sub-contract for Lot 1: National Managed Service (RM6302)</li>
                      <li>Disabled Students Allowance approved supplier </li>
                    </ul>
                  </p>

                  <h2 id="video-service">Our Video Service</h2>
                  <p>
                    Our bespoke video platform: 
                    <ul className={css.list}>
                      <li>connects Deaf users and communication professionals through secure, high-quality video calls.</li>
                      <li>Accessible on any device, it offers on-demand and pre-booked video interpreting services, reducing wait times and improving access.</li>
                      <li>UK-owned and developed with Deaf users, it meets the highest standards of privacy and security.</li>
                    </ul>
                    Read more about our <NamedLink name="VRSSalesPage">Video Interpreting Service here</NamedLink>.
                  </p>

                  <h2 id="cooperative-model">A Co-operative Model That Delivers Real Social Value</h2>
                  <p>
                    We are a co-operative, owned and shaped by the people who use and deliver and use the service. This structure drives stronger engagement from both suppliers and Deaf users, resulting in better outcomes for organisations, more expertise and more value for money.
                  </p>
                  <p>What this means in practice:</p>
                  <ul className={css.list}>
                    <li>Interpreters are more invested in success and treated with respect and valued</li>
                    <li>Deaf users have visibility, choice and confidence</li>
                    <li>Higher appointment attendance</li>
                    <li>Fewer cancellations and re-bookings</li>
                    <li>Reduced admin time and long term cost savings</li>
                  </ul>
                  <p>Social value isn't an add-on — it's built into how we operate as a community-led organisation.</p>

                  <h2 id="digital-booking">Digital Booking & Service Management</h2>
                  <p>
                    Our platform and systems connect Deaf users, interpreters and service providers. Automated SMS confirmations, seamless booking and clear monitoring information improve reliability and accountability — especially in high-volume public sector environments.
                  </p>
                  <img src={servicesSMSImage} alt="Signalise SMS confirmation" className={css.servicesImage} />

                  <h2 id="consultancy-training">Consultancy, Training & Service Design</h2>
                  <p>
                    We help organisations improve access for Deaf users, meet legal obligations and make sure your staff are supported.
                  </p>
                  <ul className={css.list}>
                    <li>Deaf awareness and accessibility training</li>
                    <li>High level stats and monitoring enabling you to report on your legal accessibility duties.</li>
                    <li>Digital and operational consultancy and support</li>
                  </ul>

                </section>
              </div>
            </div>
          </div>
        </LayoutWrapperMain>
        <LayoutWrapperFooter>
          <Footer />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

const { bool } = PropTypes;

OurServicesPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const OurServicesPage = compose(connect(mapStateToProps), injectIntl)(OurServicesPageComponent);

export default OurServicesPage;
